import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import Header from '@/components/layout/Header';
import { getImageUrl } from '@/utils/helpers';
import { 
  Bell, BellOff, Check, CheckCheck, Trash2, Heart, 
  MessageCircle, UserPlus, ShoppingBag, Calendar, 
  Info, Loader2, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// 通知类型图标和颜色
const NOTIFICATION_CONFIG = {
  follow: {
    icon: UserPlus,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
  },
  like_artwork: {
    icon: Heart,
    color: 'text-pink-500',
    bgColor: 'bg-pink-50',
  },
  like_post: {
    icon: Heart,
    color: 'text-pink-500',
    bgColor: 'bg-pink-50',
  },
  comment: {
    icon: MessageCircle,
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
  },
  message: {
    icon: MessageCircle,
    color: 'text-amber-500',
    bgColor: 'bg-purple-50',
  },
  system: {
    icon: Info,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50/50',
  },
  exhibition: {
    icon: Calendar,
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
  },
  order: {
    icon: ShoppingBag,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-50',
  },
};

const NotificationsPage = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchNotifications();
  }, [user, authLoading, filter]);

  const fetchNotifications = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = filter === 'unread' ? { unread_only: true } : {};
      const res = await axios.get(`${API}/notifications`, { headers, params });
      setNotifications(res.data);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/notifications/read/${notificationId}`, {}, { headers });
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (err) {
      toast.error('操作失败');
    }
  };

  const markAllAsRead = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/notifications/read-all`, {}, { headers });
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      toast.success('已全部标记为已读');
    } catch (err) {
      toast.error('操作失败');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/notifications/${notificationId}`, { headers });
      setNotifications(notifications.filter(n => n.id !== notificationId));
      toast.success('已删除');
    } catch (err) {
      toast.error('删除失败');
    }
  };

  const clearAll = async () => {
    if (!window.confirm('确定要清空所有通知吗？')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/notifications/clear-all`, { headers });
      setNotifications([]);
      toast.success('已清空所有通知');
    } catch (err) {
      toast.error('操作失败');
    }
  };

  const handleNotificationClick = (notification) => {
    // 标记为已读
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    // 跳转到相关页面
    if (notification.link) {
      navigate(notification.link);
    } else if (notification.from_user_id) {
      navigate(`/profile/${notification.from_user_id}`);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return '刚刚';
    if (diffMins < 60) return `${diffMins}分钟前`;
    if (diffHours < 24) return `${diffHours}小时前`;
    if (diffDays < 7) return `${diffDays}天前`;
    return date.toLocaleDateString('zh-CN');
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (authLoading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </>
    );
  }

  if (!user) return null;

  return (
    <>
      <Header />
      <div className="min-h-screen bg-amber-50/50 pt-16" data-testid="notifications-page">
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          {/* 页面标题 */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Bell className="w-6 h-6 text-amber-700" />
              <h1 className="text-2xl font-bold">消息通知</h1>
              {unreadCount > 0 && (
                <Badge className="bg-red-500 text-white">{unreadCount} 未读</Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button variant="outline" size="sm" onClick={markAllAsRead}>
                  <CheckCheck className="w-4 h-4 mr-1" />
                  全部已读
                </Button>
              )}
              {notifications.length > 0 && (
                <Button variant="outline" size="sm" onClick={clearAll} className="text-red-500 hover:text-red-600">
                  <Trash2 className="w-4 h-4 mr-1" />
                  清空
                </Button>
              )}
            </div>
          </div>

          {/* 过滤器 */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
            >
              全部
            </Button>
            <Button
              variant={filter === 'unread' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('unread')}
            >
              未读
            </Button>
          </div>

          {/* 通知列表 */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-20">
                  <BellOff className="w-16 h-16 mx-auto mb-4 text-amber-400" />
                  <p className="text-amber-600">
                    {filter === 'unread' ? '没有未读通知' : '暂无通知'}
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {notifications.map((notification) => {
                    const config = NOTIFICATION_CONFIG[notification.type] || NOTIFICATION_CONFIG.system;
                    const Icon = config.icon;
                    
                    return (
                      <div
                        key={notification.id}
                        className={`p-4 hover:bg-amber-50/50 cursor-pointer transition-colors ${!notification.is_read ? 'bg-blue-50/50' : ''}`}
                        onClick={() => handleNotificationClick(notification)}
                        data-testid={`notification-${notification.id}`}
                      >
                        <div className="flex items-start gap-4">
                          {/* 图标或头像 */}
                          <div className="flex-shrink-0">
                            {notification.from_user?.avatar ? (
                              <img loading="lazy"
                                src={getImageUrl(notification.from_user.avatar)}
                                alt=""
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <div className={`w-12 h-12 rounded-full ${config.bgColor} flex items-center justify-center`}>
                                <Icon className={`w-6 h-6 ${config.color}`} />
                              </div>
                            )}
                          </div>

                          {/* 内容 */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium text-amber-900">
                                  {notification.title}
                                </p>
                                <p className="text-sm text-amber-700/80 mt-1">
                                  {notification.content}
                                </p>
                                <p className="text-xs text-amber-500 mt-2">
                                  {formatTime(notification.created_at)}
                                </p>
                              </div>
                              
                              {/* 操作按钮 */}
                              <div className="flex items-center gap-2 ml-4">
                                {!notification.is_read && (
                                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteNotification(notification.id);
                                  }}
                                >
                                  <X className="w-4 h-4 text-amber-500" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default NotificationsPage;
